import createContext from 'contexts/create-context';
import * as React from 'react';

import {
  type MinimalGeneralSettings as MinimalGeneralSettingsType,
  useMinimalGeneralSettings as useMinimalGeneralSettingsQuery,
} from '../app/settings/models';
import { usePendingIncomingDocumentsCount } from '../app/incoming-document/models';
import { nullthrows } from '../utils/invariant';
import { SpinnerBlock } from '../components/Spinner';

export type MinimalGeneralSettings = MinimalGeneralSettingsType;

interface ProviderValue {
  settings: MinimalGeneralSettings;
  refreshData: () => void;
  incomingDocCount: number;
  refreshIncomingDocCount: () => void;
}

const [useContext, ReactProvider] = createContext<ProviderValue>();

interface IMinimalGeneralSettingsProviderProps {
  children?: React.ReactNode;
}

export const MinimalGeneralSettingsProvider: React.FC<IMinimalGeneralSettingsProviderProps> = (props) => {
  const { children } = props;
  const { data, isLoading, mutate: refreshData } = useMinimalGeneralSettingsQuery();
  const { data: incomingDocCountData, mutate: refreshIncomingDocCount } = usePendingIncomingDocumentsCount();

  React.useEffect(() => {
    // Refresh incoming doc count every 30 seconds
    const intervalRef = setInterval(() => {
      refreshIncomingDocCount();
    }, 30_000);
    return () => {
      clearInterval(intervalRef);
    };
  }, [refreshIncomingDocCount]);

  if (isLoading) {
    return <SpinnerBlock message="Loading..." className="h-screen" />;
  }

  return (
    <ReactProvider
      value={{
        settings: nullthrows(data, 'General settings not found'),
        refreshData: () => {
          refreshData();
        },
        incomingDocCount: incomingDocCountData?.count ?? 0,
        refreshIncomingDocCount: () => {
          refreshIncomingDocCount();
        },
      }}
    >
      {children}
    </ReactProvider>
  );
};

export const useMinimalGeneralSettings = useContext;
